// US-BUCKET-20 -- Unified bucket manager screen.
//
// Single screen combining:
//   1. Drag-to-reorder priority (top = highest priority = gets ceiling on uneven splits)
//   2. Inline percentage inputs per bucket with live running total
//   3. Ghost "+" card at the bottom to add a new bucket
//
// Guardian view: all cards draggable, all percentages editable.
// Child view:   guardian-managed cards pinned at top (greyed, not draggable);
//               child-managed cards draggable only among themselves.
//
// Works on both desktop (pointer/mouse) and touch (Fire tablet / Android).

import {
  type BucketEntry,
  type BucketPriorityEntry,
  type BucketRebalanceEntry,
  bucketAutoRebalance,
  bucketUpdate,
  bucketUpdatePriorities,
  bucketUpdateSplits,
} from '@chorepop/core/client';
import { type Component, For, Show, createMemo, createSignal, onMount } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';
import { AppIcon } from '../components/AppIcon';

type Props = {
  sessionId: string;
  childAccountId: number;
  childDisplayName: string;
  buckets: BucketEntry[];
  isGuardian: boolean;
  currency: string;
  onSaved: () => void;
  onCancel: () => void;
  onAddBucket: () => void;
};

function bucketColor(color: string | null): string {
  return color ?? '#888888';
}

function percentToBps(s: string): number | null {
  const n = Number.parseInt(s.trim(), 10);
  if (Number.isNaN(n) || n < 0 || n > 100) return null;
  return n * 100;
}

/** Sort initial list: guardian-managed first (by priority desc), then child-managed (by priority desc). */
function initOrder(buckets: BucketEntry[], isGuardian: boolean): BucketEntry[] {
  if (isGuardian) {
    return [...buckets].sort((a, b) => b.priority - a.priority);
  }
  const guardian = [...buckets]
    .filter((b) => b.isGuardianManaged)
    .sort((a, b) => b.priority - a.priority);
  const child = [...buckets]
    .filter((b) => !b.isGuardianManaged)
    .sort((a, b) => b.priority - a.priority);
  return [...guardian, ...child];
}

export const BucketManagerScreen: Component<Props> = (props) => {
  const [ordered, setOrdered] = createSignal<BucketEntry[]>(
    initOrder(props.buckets, props.isGuardian),
  );
  const [percents, setPercents] = createSignal<Record<number, string>>(
    Object.fromEntries(
      props.buckets.map((b) => [b.bucketId, Math.round(b.allocationBps / 100).toString()]),
    ),
  );

  const [draggingId, setDraggingId] = createSignal<number | null>(null);
  const [dragOverId, setDragOverId] = createSignal<number | null>(null);
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [autoRebalancedMsg, setAutoRebalancedMsg] = createSignal<string | null>(null);
  const [expandedInterestBucketIds, setExpandedInterestBucketIds] = createSignal<Set<number>>(
    new Set(),
  );
  const [interestDrafts, setInterestDrafts] = createSignal<
    Record<number, { enabled: boolean; annualRatePercent: string }>
  >(
    Object.fromEntries(
      props.buckets.map((bucket) => [
        bucket.bucketId,
        {
          enabled: bucket.interestEnabled ?? false,
          annualRatePercent: ((bucket.interestRateBps ?? 0) / 100).toString(),
        },
      ]),
    ),
  );

  let listRef: HTMLOListElement | undefined;

  // Auto-rebalance if total is off on mount
  onMount(async () => {
    const total = props.buckets.reduce((s, b) => s + b.allocationBps, 0);
    if (total === 10000) return;

    const result = await bucketAutoRebalance(props.sessionId, props.childAccountId);
    if (result.ok) {
      const map: Record<number, number> = Object.fromEntries(
        result.value.map((e: BucketRebalanceEntry) => [e.bucketId, e.newAllocationBps]),
      );
      setPercents(
        Object.fromEntries(
          props.buckets.map((b) => [
            b.bucketId,
            Math.round((map[b.bucketId] ?? b.allocationBps) / 100).toString(),
          ]),
        ),
      );
      setAutoRebalancedMsg(
        `Allocations were ${(total / 100).toFixed(0)}% total — auto-redistributed to 100%.`,
      );
    }
  });

  // ── Drag logic (pointer events — works on both mouse and touch) ──────────

  function canDrag(b: BucketEntry): boolean {
    return props.isGuardian || !b.isGuardianManaged;
  }

  /** Find the list item element that contains the given clientY coordinate. */
  function bucketIdAtY(clientY: number): number | null {
    if (!listRef) return null;
    const items = listRef.querySelectorAll<HTMLElement>('[data-bucket-id]');
    for (const item of items) {
      const r = item.getBoundingClientRect();
      if (clientY >= r.top && clientY < r.bottom) {
        return Number(item.dataset.bucketId);
      }
    }
    return null;
  }

  function onHandlePointerDown(e: PointerEvent, bucket: BucketEntry) {
    if (!canDrag(bucket)) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setDraggingId(bucket.bucketId);
    setDragOverId(bucket.bucketId);
    setError(null);
  }

  function onHandlePointerMove(e: PointerEvent) {
    if (draggingId() === null) return;
    const id = bucketIdAtY(e.clientY);
    if (id !== null) setDragOverId(id);
  }

  function onHandlePointerUp() {
    const srcId = draggingId();
    const dstId = dragOverId();

    if (srcId !== null && dstId !== null && srcId !== dstId) {
      const arr = [...ordered()];
      const srcIdx = arr.findIndex((b) => b.bucketId === srcId);
      const dstIdx = arr.findIndex((b) => b.bucketId === dstId);
      const dstBucket = arr[dstIdx];

      // Child cannot drag into or above the guardian section
      const allowed = props.isGuardian || dstBucket == null || !dstBucket.isGuardianManaged;
      if (allowed) {
        const [removed] = arr.splice(srcIdx, 1) as [BucketEntry];
        const adjusted = dstIdx > srcIdx ? dstIdx - 1 : dstIdx;
        arr.splice(adjusted, 0, removed);
        setOrdered(arr);
      }
    }

    setDraggingId(null);
    setDragOverId(null);
  }

  // ── Derived state ─────────────────────────────────────────────────────────

  const totalBps = createMemo(() =>
    ordered().reduce((sum, b) => {
      const bps = percentToBps(percents()[b.bucketId] ?? '');
      return sum + (bps ?? 0);
    }, 0),
  );

  const interestDraftFor = (bucket: BucketEntry) =>
    interestDrafts()[bucket.bucketId] ?? {
      enabled: bucket.interestEnabled ?? false,
      annualRatePercent: ((bucket.interestRateBps ?? 0) / 100).toString(),
    };

  const parsedInterestRateBps = (bucket: BucketEntry): number | null => {
    const draft = interestDraftFor(bucket);
    if (!draft.enabled) return 0;
    const rate = Number(draft.annualRatePercent);
    if (!Number.isFinite(rate) || rate <= 0 || rate > 100) return null;
    return Math.round(rate * 100);
  };

  const hasValidInterestDrafts = createMemo(() =>
    props.buckets.every((bucket) => parsedInterestRateBps(bucket) !== null),
  );
  // The named presets describe a three-bucket split. Applying one to a
  // larger bucket list previously assigned 0% to every extra bucket, which
  // created an invalid total (for example 6,000 bps across four buckets).
  // Existing families with more buckets keep their explicit allocations.
  const supportsThreeWayPresets = createMemo(() => ordered().length === 3);
  // Children can always see the familiar examples, but applying one must
  // never change a guardian-managed bucket behind the guardian's back.
  const canApplyPreset = createMemo(
    () => props.isGuardian || ordered().every((bucket) => !bucket.isGuardianManaged),
  );
  const canSave = createMemo(() => !busy() && totalBps() === 10000 && hasValidInterestDrafts());

  // ── Save ──────────────────────────────────────────────────────────────────

  async function handleSave() {
    if (!canSave()) return;
    setBusy(true);
    setError(null);

    // Build splits — guardian-managed values locked for child callers
    const splits = ordered().map((b) => ({
      bucketId: b.bucketId,
      allocationBps:
        !props.isGuardian && b.isGuardianManaged
          ? b.allocationBps
          : (percentToBps(percents()[b.bucketId] ?? '') ?? b.allocationBps),
    }));

    const splitResult = await bucketUpdateSplits(props.sessionId, props.childAccountId, splits);
    if (!splitResult.ok) {
      setError(splitResult.error.message);
      setBusy(false);
      return;
    }

    // Build priorities from current order: top item = highest priority number
    const n = ordered().length;
    const prioEntries: BucketPriorityEntry[] = ordered().map((b, i) => ({
      bucketId: b.bucketId,
      priority: n - i,
    }));

    // In child view, only submit priorities for buckets the child can move
    const filteredPrio = props.isGuardian
      ? prioEntries
      : prioEntries.filter((_, i) => !ordered()[i]?.isGuardianManaged);

    if (filteredPrio.length > 0) {
      const prioResult = await bucketUpdatePriorities(
        props.sessionId,
        props.childAccountId,
        filteredPrio,
      );
      if (!prioResult.ok) {
        setError(prioResult.error.message);
        setBusy(false);
        return;
      }
    }

    // Interest belongs to an individual bucket, but is edited in this
    // guardian bucket workspace so a family can see the complete split and
    // its learning rules together. Child callers never receive these fields.
    if (props.isGuardian) {
      for (const bucket of ordered()) {
        const draft = interestDraftFor(bucket);
        const rateBps = parsedInterestRateBps(bucket);
        if (rateBps === null) {
          setError(
            `Enter an annual interest rate between 0.01% and 100% for ${bucket.bucketName}.`,
          );
          setBusy(false);
          return;
        }
        const changed =
          draft.enabled !== (bucket.interestEnabled ?? false) ||
          rateBps !== (bucket.interestRateBps ?? 0) ||
          // BUG-035: interest configured through the guardian UI is real
          // credited interest. Saving also upgrades a legacy enabled,
          // display-only bucket so future accruals change its balance.
          draft.enabled !== (bucket.interestActuallyCredits ?? false);
        if (!changed) continue;
        const interestResult = await bucketUpdate({
          sessionId: props.sessionId,
          bucketId: bucket.bucketId,
          interestEnabled: draft.enabled,
          interestRateBps: rateBps,
          // This compact editor collects an annual rate, so a newly enabled
          // example must also receive a valid yearly cadence. Without this,
          // the backend rightly rejects an otherwise valid toggle because a
          // legacy bucket has no interest period yet.
          interestPeriodCode: draft.enabled ? (bucket.interestPeriodCode ?? 'yearly') : null,
          interestActuallyCredits: draft.enabled,
        });
        if (!interestResult.ok) {
          setError(interestResult.error.message);
          setBusy(false);
          return;
        }
      }
    }

    props.onSaved();
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const remainingBps = createMemo(() => 10000 - totalBps());

  const totalClass = createMemo(() => {
    const t = totalBps();
    if (t === 10000) return 'bm-total bm-total--ok';
    if (t > 10000) return 'bm-total bm-total--over';
    return 'bm-total bm-total--under';
  });

  function setPercent(bucketId: number, value: string) {
    setPercents((current) => ({ ...current, [bucketId]: value }));
    setError(null);
  }

  function adjustPercent(bucketId: number, delta: number) {
    const current = percentToBps(percents()[bucketId] ?? '0') ?? 0;
    const next = Math.max(0, Math.min(10000, current + delta * 100));
    const otherTotal = totalBps() - current;
    if (delta > 0 && otherTotal + next > 10000) return;
    setPercent(bucketId, String(Math.round(next / 100)));
  }

  function applyPreset(values: number[]) {
    if (!supportsThreeWayPresets()) {
      setError('Split presets are available when this child has exactly three buckets.');
      return;
    }
    if (!canApplyPreset()) {
      setError('A guardian manages part of this split. Ask them to apply a preset.');
      return;
    }
    setPercents(
      Object.fromEntries(
        ordered().map((bucket, index) => [bucket.bucketId, String(values[index] ?? 0)]),
      ),
    );
    setError(null);
  }

  function toggleInterestDetails(bucketId: number) {
    setExpandedInterestBucketIds((current) => {
      const next = new Set(current);
      if (next.has(bucketId)) next.delete(bucketId);
      else next.add(bucketId);
      return next;
    });
  }

  function updateInterestDraft(
    bucket: BucketEntry,
    update: Partial<{ enabled: boolean; annualRatePercent: string }>,
  ) {
    setInterestDrafts((current) => ({
      ...current,
      [bucket.bucketId]: { ...interestDraftFor(bucket), ...update },
    }));
    setError(null);
  }

  const bucketShare = (bucketId: number) =>
    Math.max(percentToBps(percents()[bucketId] ?? '0') ?? 0, 0);

  return (
    <ActionScreen
      class="action-screen--bucket-manager"
      eyebrow={`${props.childDisplayName}'s split`}
      title="Buckets"
      onBack={props.onCancel}
      footer={
        <button
          type="button"
          class="primary action-screen-primary"
          onClick={handleSave}
          disabled={!canSave()}
          aria-busy={busy()}
        >
          {busy() ? 'Saving…' : 'Save split'}
        </button>
      }
    >
      <Show when={autoRebalancedMsg()}>
        <div class="form-info" role="status">
          {autoRebalancedMsg()}
        </div>
      </Show>

      <section class="bucket-split-summary" aria-label="Current bucket split">
        <div class="bucket-split-summary-head">
          <span class="action-screen-eyebrow">Every payment splits</span>
          <span class={`bucket-split-total${totalBps() === 10000 ? ' is-valid' : ' is-invalid'}`}>
            {totalBps() === 10000 ? '✓ 100%' : `${totalBps() / 100}%`}
          </span>
        </div>
        <div class="bucket-split-bar" aria-hidden="true">
          <For each={ordered()}>
            {(bucket) => (
              <span
                style={`--bucket-share: ${bucketShare(bucket.bucketId)}; --bucket-color: ${bucket.displayColor ?? 'var(--brand)'}`}
              />
            )}
          </For>
        </div>
        <div class="bucket-split-labels">
          <For each={ordered()}>
            {(bucket) => (
              <span>
                {bucket.bucketName} {Math.round(bucketShare(bucket.bucketId) / 100)}%
              </span>
            )}
          </For>
        </div>
      </section>

      <Show when={supportsThreeWayPresets()}>
        <div class="bucket-split-presets" aria-label="Split presets">
          <span class="action-screen-eyebrow">Presets</span>
          <button
            type="button"
            class="action-chip"
            data-testid="bucket-split-preset-50-30-20"
            disabled={!canApplyPreset()}
            onClick={() => applyPreset([50, 30, 20])}
          >
            50 / 30 / 20
          </button>
          <button
            type="button"
            class="action-chip"
            data-testid="bucket-split-preset-60-30-10"
            disabled={!canApplyPreset()}
            onClick={() => applyPreset([60, 30, 10])}
          >
            60 / 30 / 10
          </button>
          <button
            type="button"
            class="action-chip"
            data-testid="bucket-split-preset-80-10-10"
            disabled={!canApplyPreset()}
            onClick={() => applyPreset([80, 10, 10])}
          >
            80 / 10 / 10
          </button>
        </div>
        <Show when={!canApplyPreset()}>
          <p class="field-hint">
            You can look at these examples, but your guardian manages one or more buckets in this
            split.
          </p>
        </Show>
      </Show>

      <p class="bm-legend">
        Drag a bucket to change priority. Use the controls to set how every payment splits.
      </p>

      <ol
        class="bm-list"
        ref={listRef}
        onPointerMove={onHandlePointerMove}
        onPointerUp={onHandlePointerUp}
        onPointerCancel={onHandlePointerUp}
      >
        <For each={ordered()}>
          {(b) => {
            const isLocked = !props.isGuardian && b.isGuardianManaged;
            const isDragging = createMemo(() => draggingId() === b.bucketId);
            const isDragOver = createMemo(
              () => dragOverId() === b.bucketId && draggingId() !== b.bucketId,
            );

            return (
              <li
                class={[
                  'bm-item',
                  isLocked ? 'bm-item--locked' : '',
                  isDragging() ? 'bm-item--dragging' : '',
                  isDragOver() ? 'bm-item--drag-over' : '',
                ].join(' ')}
                data-bucket-id={b.bucketId}
              >
                {/* Drag handle */}
                <span
                  class={`bm-handle${isLocked ? ' bm-handle--disabled' : ''}`}
                  aria-label={isLocked ? 'Locked — guardian managed' : 'Drag to reorder'}
                  onPointerDown={(e) => onHandlePointerDown(e, b)}
                  onPointerMove={onHandlePointerMove}
                  onPointerUp={onHandlePointerUp}
                >
                  <Show when={isLocked} fallback="⠿">
                    <AppIcon name="lock" />
                  </Show>
                </span>

                {/* Colour swatch */}
                <span
                  class="bm-swatch"
                  style={{ background: bucketColor(b.displayColor) }}
                  aria-hidden="true"
                />

                {/* Name */}
                <span class="bm-name">
                  {b.bucketName}
                  <Show when={b.isGuardianManaged}>
                    <span class="bm-badge">Guardian</span>
                  </Show>
                </span>

                {/* Percentage input */}
                <Show
                  when={!isLocked}
                  fallback={<span class="bm-pct-locked">{Math.round(b.allocationBps / 100)}%</span>}
                >
                  <div class="bm-pct-wrap">
                    <button
                      type="button"
                      class="bucket-split-stepper-button"
                      onClick={() => adjustPercent(b.bucketId, -1)}
                      disabled={busy() || (percentToBps(percents()[b.bucketId] ?? '0') ?? 0) <= 0}
                      aria-label={`Decrease ${b.bucketName} percentage`}
                    >
                      −
                    </button>
                    <input
                      type="number"
                      class="bm-pct-input"
                      min="0"
                      max="100"
                      step="1"
                      inputMode="numeric"
                      value={percents()[b.bucketId] ?? ''}
                      onInput={(e) => setPercent(b.bucketId, e.currentTarget.value)}
                      disabled={busy()}
                      aria-label={`${b.bucketName} percentage`}
                    />
                    <span class="bm-pct-sym">%</span>
                    <button
                      type="button"
                      class="bucket-split-stepper-button"
                      onClick={() => adjustPercent(b.bucketId, 1)}
                      disabled={busy() || totalBps() >= 10000}
                      aria-label={`Increase ${b.bucketName} percentage`}
                    >
                      +
                    </button>
                  </div>
                </Show>
                <Show when={props.isGuardian}>
                  <button
                    type="button"
                    class="bm-interest-toggle"
                    data-testid={`bucket-interest-toggle-${b.bucketId}`}
                    aria-expanded={expandedInterestBucketIds().has(b.bucketId)}
                    aria-controls={`bucket-interest-${b.bucketId}`}
                    onClick={() => toggleInterestDetails(b.bucketId)}
                  >
                    Interest
                  </button>
                </Show>
                <Show when={!props.isGuardian && (b.interestEnabled ?? false)}>
                  <span class="bm-badge">Interest on</span>
                </Show>
                <Show when={props.isGuardian && expandedInterestBucketIds().has(b.bucketId)}>
                  <div
                    id={`bucket-interest-${b.bucketId}`}
                    class="bm-interest-panel"
                    data-testid={`bucket-interest-panel-${b.bucketId}`}
                  >
                    <label class="bm-interest-enabled">
                      <input
                        type="checkbox"
                        checked={interestDraftFor(b).enabled}
                        disabled={busy()}
                        onChange={(event) =>
                          updateInterestDraft(b, { enabled: event.currentTarget.checked })
                        }
                        data-testid={`bucket-interest-enabled-${b.bucketId}`}
                      />
                      <span>Credit interest to this bucket</span>
                    </label>
                    <Show when={interestDraftFor(b).enabled}>
                      <label class="bm-interest-rate">
                        <span>Annual interest rate</span>
                        <input
                          type="number"
                          data-testid={`bucket-interest-rate-${b.bucketId}`}
                          inputMode="decimal"
                          min="0.01"
                          max="100"
                          step="0.01"
                          value={interestDraftFor(b).annualRatePercent}
                          disabled={busy()}
                          onInput={(event) =>
                            updateInterestDraft(b, { annualRatePercent: event.currentTarget.value })
                          }
                          aria-label={`${b.bucketName} annual interest rate`}
                        />
                        <span>%</span>
                      </label>
                    </Show>
                    <p>
                      When enabled, each interest payment is added to this bucket and changes its
                      balance.
                    </p>
                  </div>
                </Show>
              </li>
            );
          }}
        </For>

        {/* Ghost "+" add card */}
        <li class="bm-item bm-item--add">
          <button
            type="button"
            class="bm-add-button"
            onClick={props.onAddBucket}
            data-testid="bucket-manager-add"
          >
            <span class="bm-add-icon" aria-hidden="true">
              +
            </span>
            <span class="bm-add-label">New bucket</span>
          </button>
        </li>
      </ol>

      {/* Live running total */}
      <div class={totalClass()} role="status" aria-live="polite">
        <span class="bm-total__label">Total</span>
        <span class="bm-total__value">{(totalBps() / 100).toFixed(0)}%</span>
        <Show when={remainingBps() !== 0}>
          <span class="bm-total__hint">
            {remainingBps() > 0
              ? `${(remainingBps() / 100).toFixed(0)}% unallocated`
              : `${(Math.abs(remainingBps()) / 100).toFixed(0)}% over`}
          </span>
        </Show>
      </div>

      <Show when={error()}>
        <p class="form-error" role="alert">
          {error()}
        </p>
      </Show>
    </ActionScreen>
  );
};
